import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/v2/button";
import { Input } from "@/components/ui/v2/input";
import { Label } from "@/components/ui/v2/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { SortableList } from "@/components/ui/v2/sortable-list";
import { useDescriptionGroup } from "@/lib/contexts/DescriptionGroup/useDescriptionGroup";
import { usePrompt } from "@/lib/hooks/usePrompt";
import useToast from "@/lib/hooks/useToast";
import {
  OrderedSectionName,
  UpdateClientDescriptionsOrderRequest,
} from "@/types/request/UpdateClientDescriptionsOrderRequest";
import { GetDescriptionGroupsResponse } from "@/types/response/GetDescriptionGroupsResponse";

type GroupClientDescriptionsFormProps = {
  clientId: string;
  tenantId: string | null;
};

type GroupClientDescriptionsFormContentProps =
  GroupClientDescriptionsFormProps & {
    name: string;
    onNameChange: (name: string) => void;
  };

const groupClientDescriptionsSchema = z.object({
  groupName: z.string(),
  orderedSectionNames: z.array(
    z.object({
      customHeader: z.string().nullable(),
      descriptionSectionName: z.string(),
      label: z.string().nullable(),
      isEnabled: z.boolean(),
      displayOrder: z.number(),
    })
  ),
  tenantId: z.string().nullable(),
});

type GroupClientDescriptionsFormData = z.infer<
  typeof groupClientDescriptionsSchema
>;

const createOrderedSectionNames = (
  groupName: string | null,
  descriptionGroups: GetDescriptionGroupsResponse[] | undefined
): OrderedSectionName[] => {
  if (!groupName || !descriptionGroups) return [];

  const selectedGroupData = descriptionGroups.find(
    (group) => group.name === groupName
  );
  if (!selectedGroupData) return [];

  const groupDescriptions = selectedGroupData.descriptions || [];

  const defaultOrder = [
    { name: "Summary", order: 1, label: "Summary" },
    { name: "FunctionDescription", order: 2, label: "Function Description" },
    { name: "ClientDescription", order: 3, label: "Client Description" },
    { name: "RequirementsDescription", order: 4, label: "Requirements Description" },
    { name: "OfferDescription", order: 5, label: "Offer Description" },
    { name: "AdditionalDescription", order: 6, label: "Additional Description" },
  ];

  return groupDescriptions.map((description) => {
    const defaultDesc = defaultOrder.find(
      (d) => d.name === description.descriptionSectionName
    );
    return {
      customHeader: description.customHeader ?? null,
      descriptionSectionName: description.descriptionSectionName,
      label: description.label ?? defaultDesc?.label ?? description.descriptionSectionName,
      isEnabled: description.isEnabled ?? true,
      displayOrder: description.displayOrder ?? defaultDesc?.order ?? 1,
    };
  });
};

const GroupClientDescriptionsFormContent = ({
  tenantId,
  onNameChange,
}: GroupClientDescriptionsFormContentProps) => {
  const {
    descriptionGroups,
    descriptionGroupsLoading,
    updateClientDescriptionsOrder,
  } = useDescriptionGroup();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<GroupClientDescriptionsFormData>({
    resolver: zodResolver(groupClientDescriptionsSchema),
    defaultValues: {
      groupName: "",
      orderedSectionNames: [],
      tenantId,
    },
  });

  const { markSaved, confirmDialog } = usePrompt(
    {
      title: t("prompts.unsavedChanges.title"),
      description: t("prompts.unsavedChanges.description"),
    },
    isDirty
  );

  const groupName = watch("groupName");

  useEffect(() => {
    if (descriptionGroups?.length && !groupName) {
      const enabledGroups = descriptionGroups.filter((group) => group.isEnabled);
      if (enabledGroups.length > 0) {
        const highestPriorityGroup = enabledGroups.reduce((min, group) =>
          group.priority < min.priority ? group : min,
        enabledGroups[0]
        );
        setValue("groupName", highestPriorityGroup.name);
        onNameChange(highestPriorityGroup.name);
      }
    }
  }, [descriptionGroups, groupName, setValue, onNameChange]);

  useEffect(() => {
    if (groupName && descriptionGroups && !descriptionGroupsLoading) {
      const selectedGroup = descriptionGroups.find(
        (group) => group.name === groupName
      );
      if (selectedGroup && selectedGroup.descriptions) {
        const orderedSectionNames = createOrderedSectionNames(
          groupName,
          descriptionGroups
        );
        setValue("orderedSectionNames", orderedSectionNames);
      }
    }
  }, [groupName, descriptionGroups, descriptionGroupsLoading, setValue]);

  const handleFormSubmit = (data: GroupClientDescriptionsFormData) => {
    const sortedOrderedSectionNames = [...data.orderedSectionNames].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );

    const submitData: UpdateClientDescriptionsOrderRequest = {
      groupName: data.groupName,
      orderedSectionNames: sortedOrderedSectionNames,
      tenantId: data.tenantId,
    };

    updateClientDescriptionsOrder.mutate(submitData, {
      onSuccess: () => {
        showToast(t("common.success"));
        markSaved();
        reset(data);
      },
      onError: () => {
        showToast(t("common.error"), "error");
      },
    });
  };

  if (descriptionGroupsLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Label>{t("common.publicationChannel")}</Label>
        <Controller
          name="groupName"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={(value) => {
              field.onChange(value);
              onNameChange(value);
            }}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("common.publicationChannel")} />
              </SelectTrigger>
              <SelectContent>
                {(descriptionGroups ?? []).map((group) => (
                  <SelectItem
                    key={group.name}
                    value={group.name}
                    disabled={!group.isEnabled}
                  >
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-foreground">
          {`${t("common.description")} ${t("common.priority")}`}
        </h3>
        <Controller
          name="orderedSectionNames"
          control={control}
          render={({ field }) => (
            <SortableList<OrderedSectionName>
              items={
                [...field.value].sort((a, b) => a.displayOrder - b.displayOrder)
              }
              itemKey="descriptionSectionName"
              onReorder={(newOrder) => {
                const updatedItems = newOrder.map((item, index) => ({
                  ...item,
                  displayOrder: index + 1,
                }));
                field.onChange(updatedItems);
              }}
              renderItem={(item) => (
                <div className={`flex w-full flex-col gap-3 transition-opacity ${!item.isEnabled ? "opacity-40" : ""}`}>
                  <div className="flex w-full items-center justify-between">
                    <h4 className={`text-sm font-medium ${item.isEnabled ? "text-foreground" : "text-muted-foreground line-through"}`}>
                      {item.label || item.descriptionSectionName}
                    </h4>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => {
                        const updatedItems = field.value.map((i) =>
                          i.descriptionSectionName === item.descriptionSectionName
                            ? { ...i, isEnabled: !i.isEnabled }
                            : i
                        );
                        field.onChange(updatedItems);
                      }}
                    >
                      {item.isEnabled ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5 opacity-50" />
                      )}
                    </button>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">{t("common.customHeader")}</Label>
                    <Input
                      value={item.customHeader || ""}
                      onChange={(e) => {
                        const updatedItems = field.value.map((i) =>
                          i.descriptionSectionName === item.descriptionSectionName
                            ? { ...i, customHeader: e.target.value }
                            : i
                        );
                        field.onChange(updatedItems);
                      }}
                    />
                  </div>
                </div>
              )}
            />
          )}
        />

        {errors.orderedSectionNames && (
          <p className="text-sm text-destructive">
            {errors.orderedSectionNames.message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          {isDirty && (
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                const orderedSectionNames = createOrderedSectionNames(
                  groupName,
                  descriptionGroups
                );
                reset({ groupName, orderedSectionNames, tenantId });
              }}
            >
              {t("common.cancel")}
            </Button>
          )}
          <Button type="submit" disabled={!isDirty || updateClientDescriptionsOrder.isPending}>
            {updateClientDescriptionsOrder.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {t("common.save")}
          </Button>
        </div>
      </div>
      {confirmDialog}
    </form>
  );
};

const GroupClientDescriptionsForm = ({
  clientId,
  tenantId,
}: GroupClientDescriptionsFormProps) => {
  const [name, setName] = useState("Default");

  return (
    <GroupClientDescriptionsFormContent
      clientId={clientId}
      name={name}
      onNameChange={setName}
      tenantId={tenantId}
    />
  );
};

export default GroupClientDescriptionsForm;
